import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Loader2 } from 'lucide-react';
import { Plus, PencilSimple, Trash, BookOpen, Eye, PencilLine, Sparkle, MagicWand } from '@phosphor-icons/react';
import { MarkdownPreview } from '../components/MarkdownPreview';
import { listMissionTemplates, createMissionTemplate, updateMissionTemplate, deleteMissionTemplate, generateMissionTemplateAI } from '../services/api';
import { PageHero } from '../components/motion/PageHero';

const EMPTY = { label: '', icon: '📄', objet: '', resume: '', is_public: false };

export default function MissionTemplatesAdminPage() {
  const { user } = useAuth();
  const isAdmin = user && ['super_admin', 'admin', 'direction'].includes(user.role);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = création
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [aiIntention, setAiIntention] = useState('');
  const [aiLoading, setAiLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setItems((await listMissionTemplates()).data.items || []); }
    catch { toast.error('Chargement impossible'); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  if (!user) return <Navigate to="/login" replace />;
  if (!isAdmin) return <div className="p-8 text-[#5E6D66]">Accès réservé aux administrateurs.</div>;

  const openNew = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
  const openEdit = (t) => { setEditing(t); setForm({ label: t.label, icon: t.icon || '📄', objet: t.objet, resume: t.resume, is_public: !!t.is_public }); setDialogOpen(true); };

  const save = async () => {
    if (!form.label || !form.objet || !form.resume) {
      toast.error('Libellé, objet et résumé sont obligatoires');
      return;
    }
    setSaving(true);
    try {
      if (editing) await updateMissionTemplate(editing.id, form);
      else await createMissionTemplate(form);
      toast.success(editing ? 'Modèle mis à jour' : 'Modèle créé');
      setDialogOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Enregistrement impossible'); }
    setSaving(false);
  };

  const remove = async (t) => {
    if (!window.confirm(`Supprimer le modèle « ${t.label} » ?`)) return;
    try { await deleteMissionTemplate(t.id); toast.success('Supprimé'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Suppression impossible'); }
  };

  return (
    <div className="p-6 md:p-8 space-y-6 max-w-5xl mx-auto" data-testid="templates-admin-page">
      {/* Hero header — UX transversale v2 */}
      <PageHero
        title="Bibliothèque de modèles"
        subtitle="Modèles de missions ESS partagés — CRUD admin"
        icon={<BookOpen size={24} weight="duotone" className="text-white" />}
        gradient="from-[#5E6D66] to-[#2D5A4C]"
        accents={['#80C51F', '#FF8E00']}
      />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#1A2421] flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <BookOpen size={22} className="text-[#2D5A4C]" weight="duotone" /> Bibliothèque de modèles de missions
          </h1>
          <p className="text-sm text-[#5E6D66]">Ces modèles apparaissent dans le menu « 📖 Modèles » de la modale Nouveau dossier.</p>
        </div>
        <Button className="bg-[#2D5A4C] hover:bg-[#1E4136]" onClick={openNew} data-testid="template-new">
          <Plus size={14} className="mr-1" /> Nouveau modèle
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-[#5E6D66]"><Loader2 className="animate-spin" size={14} /> Chargement…</div>
      ) : items.length === 0 ? (
        <Card><CardContent className="p-6 text-center text-[#5E6D66]">Aucun modèle. Créez-en un.</CardContent></Card>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {items.map((t) => (
            <Card key={t.id} className="border-[#E8E6E1]" data-testid={`template-card-${t.id}`}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center justify-between gap-2">
                  <span className="flex items-center gap-2"><span className="text-xl">{t.icon || '📄'}</span> {t.label}</span>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(t)} data-testid={`edit-${t.id}`}><PencilSimple size={14} /></Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(t)} className="text-red-600" data-testid={`del-${t.id}`}><Trash size={14} /></Button>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="text-xs space-y-2">
                <p><span className="uppercase text-[10px] tracking-wider text-[#5E6D66] font-bold">Objet :</span> {t.objet}</p>
                <div className="max-h-32 overflow-hidden relative">
                  <MarkdownPreview text={t.resume} className="text-[11px] leading-relaxed" testId={`preview-${t.id}`} />
                  <div className="absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-white to-transparent pointer-events-none" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl" data-testid="template-dialog">
          <DialogHeader><DialogTitle>{editing ? 'Modifier le modèle' : 'Nouveau modèle de mission'}</DialogTitle></DialogHeader>
          {!editing && (
            <div className="rounded-xl border border-[#2D5A4C]/25 bg-gradient-to-br from-[#2D5A4C]/5 to-[#00AEEF]/5 p-3 space-y-2" data-testid="tpl-ai-panel">
              <div className="flex items-center gap-1.5">
                <MagicWand size={14} className="text-[#2D5A4C]" weight="fill" />
                <p className="text-[11px] font-semibold uppercase tracking-wider text-[#2D5A4C]">Générer avec l'IA</p>
              </div>
              <p className="text-[11px] text-[#5E6D66]">Décrivez le modèle en 1 phrase — l'IA remplit libellé, icône, objet et résumé markdown.</p>
              <div className="flex gap-2">
                <Input
                  value={aiIntention}
                  onChange={(e) => setAiIntention(e.target.value)}
                  placeholder="Ex : accompagner une TPE à devenir SCOP"
                  className="text-sm h-9"
                  data-testid="tpl-ai-intention"
                  maxLength={500}
                />
                <Button
                  onClick={async () => {
                    if (aiIntention.trim().length < 5) return toast.error('Décrivez le modèle (5 caractères min)');
                    setAiLoading(true);
                    try {
                      const r = await generateMissionTemplateAI(aiIntention);
                      setForm({ label: r.data.label, icon: r.data.icon, objet: r.data.objet, resume: r.data.resume });
                      toast.success("Modèle prérempli par l'IA");
                    } catch (err) { toast.error(err?.response?.data?.detail || 'Erreur IA'); }
                    setAiLoading(false);
                  }}
                  disabled={aiLoading}
                  size="sm"
                  className="bg-gradient-to-r from-[#2D5A4C] to-[#00AEEF] text-white hover:opacity-90"
                  data-testid="tpl-ai-generate"
                >
                  {aiLoading ? <Loader2 className="animate-spin mr-1" size={12}/> : <Sparkle size={12} weight="fill" className="mr-1"/>}Générer
                </Button>
              </div>
            </div>
          )}
          <div className="space-y-3">
            <div className="grid grid-cols-[80px_1fr] gap-2">
              <div><Label>Icône</Label><Input value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} maxLength={4} /></div>
              <div><Label>Libellé *</Label><Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Ex : Audit ESS" data-testid="template-label" /></div>
            </div>
            <div><Label>Objet du besoin *</Label><Input value={form.objet} onChange={(e) => setForm({ ...form, objet: e.target.value })} data-testid="template-objet" /></div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label>Résumé exécutif *</Label>
                <button type="button" onClick={() => setPreviewMode((v) => !v)} className="text-[10px] font-semibold text-[#2D5A4C] hover:underline flex items-center gap-1" data-testid="template-toggle-preview">
                  {previewMode ? <><PencilLine size={11}/> Édition</> : <><Eye size={11}/> Aperçu Markdown</>}
                </button>
              </div>
              {previewMode ? (
                <div className="border border-[#E8E6E1] rounded-md p-3 min-h-[168px] bg-[#F9F8F5]" data-testid="template-resume-preview">
                  <MarkdownPreview text={form.resume} className="text-sm" />
                  {(!form.resume || form.resume.trim() === '') && <p className="text-xs text-[#5E6D66] italic">L'aperçu apparaîtra ici. Utilisez <code>## Titre</code>, <code>- liste</code>, <code>**gras**</code>.</p>}
                </div>
              ) : (
                <Textarea rows={7} value={form.resume} onChange={(e) => setForm({ ...form, resume: e.target.value })} data-testid="template-resume" placeholder="## Contexte&#10;Description du contexte…&#10;&#10;## Enjeux&#10;- Enjeu 1&#10;- Enjeu 2&#10;&#10;## Objectifs&#10;1. Étape 1&#10;2. Étape 2" />
              )}
              <p className="text-[10px] text-[#5E6D66] mt-1 italic">Astuce : <code className="bg-[#F5F4EF] px-1 rounded">## Titre</code>, <code className="bg-[#F5F4EF] px-1 rounded">- liste</code>, <code className="bg-[#F5F4EF] px-1 rounded">**gras**</code></p>
            </div>
          </div>
          <div className="flex items-center gap-2 pt-2 border-t border-[#E8E6E1] mt-3">
            <input
              type="checkbox"
              id="tpl-is-public"
              checked={!!form.is_public}
              onChange={(e) => setForm({ ...form, is_public: e.target.checked })}
              className="w-4 h-4 accent-[#2D5A4C]"
              data-testid="template-is-public"
            />
            <label htmlFor="tpl-is-public" className="text-xs text-[#1A2421] cursor-pointer">
              <span className="font-semibold">Publier dans la bibliothèque publique</span>
              <span className="text-[#5E6D66]"> — visible sur <code className="bg-[#F5F4EF] px-1 rounded">/portal/library</code></span>
            </label>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>Annuler</Button>
            <Button className="bg-[#2D5A4C] hover:bg-[#1E4136]" onClick={save} disabled={saving} data-testid="template-save">
              {saving ? <Loader2 className="animate-spin mr-1" size={14} /> : null}
              {editing ? 'Enregistrer' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
